// ManageGoalsDialog.jsx - Manage Goals for User KPI Assignment

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Paper,
    Tooltip,
    alpha,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import AddEditGoalDialog from './AddEditGoalDialog';
import DeleteGoalConfirmDialog from './DeleteGoalConfirmDialog';

const ManageGoalsDialog = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const open = useSelector(selectors.selectManageGoalsDialogOpen);
    const dialogUserKpi = useSelector(selectors.selectDialogUserKpi);
    const userKpiWithGoals = useSelector(selectors.selectUserKpiWithGoals);
    const dialogLoading = useSelector(selectors.selectDialogLoading);

    const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const dataFetchedRef = useRef(false);
    const prevOpenRef = useRef(false);

    // Fetch user KPI with goals when dialog opens (only once per opening)
    useEffect(() => {
        // Detect when dialog just opened
        const justOpened = open && !prevOpenRef.current;
        
        if (justOpened && dialogUserKpi) {
            dispatch(actions.fetchUserKpiWithGoalsRequest(
                dialogUserKpi.userId,
                dialogUserKpi.periodId
            ));
            dataFetchedRef.current = true;
        }
        
        // Reset when dialog closes
        if (!open && prevOpenRef.current) {
            dataFetchedRef.current = false;
            setHasChanges(false);
        }
        
        prevOpenRef.current = open;
    }, [open, dialogUserKpi, dispatch]);

    const handleClose = () => {
        dispatch(actions.closeManageGoalsDialog());
        // Only refetch the table if changes were made
        if (hasChanges) {
            dispatch(actions.fetchUserKpisRequest());
        }
    };

    const handleAddGoal = () => {
        setSelectedGoal(null);
        setIsEditMode(false);
        setAddEditDialogOpen(true);
    };

    const handleEditGoal = (goal) => {
        setSelectedGoal(goal);
        setIsEditMode(true);
        setAddEditDialogOpen(true);
    };

    const handleDeleteGoal = (goal) => {
        setSelectedGoal(goal);
        setDeleteDialogOpen(true);
    };

    const handleAddEditDialogClose = () => {
        setAddEditDialogOpen(false);
        setSelectedGoal(null);
        setIsEditMode(false);
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedGoal(null);
    };

    const handleAddEditDialogSubmit = (goalData) => {
        if (!userKpiWithGoals) return;

        if (isEditMode && selectedGoal) {
            // Update existing goal - pass userKpiId and the MASTER goal ID (not the userKpiGoal record id)
            dispatch(actions.updateUserKpiGoalRequest(
                userKpiWithGoals.id,
                selectedGoal.goalId,  // This is the master goal ID reference
                goalData
            ));
        } else {
            // Add new goal
            dispatch(actions.addGoalToUserKpiRequest(
                userKpiWithGoals.id,
                goalData
            ));
        }

        setHasChanges(true);
        handleAddEditDialogClose();
    };

    const handleDeleteConfirm = () => {
        if (!userKpiWithGoals || !selectedGoal) return;

        dispatch(actions.removeGoalFromUserKpiRequest(
            selectedGoal.userKpiId,
            selectedGoal.goalId
        ));

        setHasChanges(true);
        handleDeleteDialogClose();
    };

    const goals = userKpiWithGoals?.goals || [];
    const totalWeight = goals.reduce((sum, g) => sum + (parseFloat(g.weight) || 0), 0);

    const getScoreLabel = (weight) => {
        const score = parseFloat(weight) || 0;
        const labels = {
            1: 'Lowest',
            2: 'Low',
            3: 'Medium',
            4: 'High',
            5: 'Highest'
        };
        const label = labels[score] || '';
        return label || `${score}`;
    };

    const getWeightColor = (weight) => {
        const score = parseFloat(weight) || 0;
        const colors = {
            1: 'error',
            2: 'warning',
            3: 'info',
            4: 'primary',
            5: 'success'
        };
        return colors[score] || 'default';
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth disableRestoreFocus>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">Manage Goals</Typography>
                            {userKpiWithGoals && (
                                <Typography variant="caption" color="text.secondary">
                                    {userKpiWithGoals.user?.email || userKpiWithGoals.userId} - {userKpiWithGoals.period?.name}
                                </Typography>
                            )}
                        </Box>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {dialogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Summary Section */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Goals: <strong>{goals.length}</strong>
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            Total Weight: <strong>{totalWeight.toFixed(0)}</strong>
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddGoal}
                                        size="small"
                                    >
                                        Add Goal
                                    </Button>
                                </Box>
                            </Box>

                            {/* Goals Table */}
                            {goals.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No goals assigned yet. Click "Add Goal" to get started.
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.divider, 0.06) }}>Goal</TableCell>
                                                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.divider, 0.06) }}>Category</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.divider, 0.06) }}>Weight</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.divider, 0.06), minWidth: 120 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {goals.map((goal) => (
                                                <TableRow key={goal.id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {goal.goal?.name || 'Unknown Goal'}
                                                        </Typography>
                                                        {goal.goal?.description && (
                                                            <Tooltip title={goal.goal.description} arrow>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary"
                                                                    sx={{ 
                                                                        mt: 0.5,
                                                                        display: 'block',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        maxWidth: 400
                                                                    }}
                                                                >
                                                                    {goal.goal.description}
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={goal.category} 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={`Weight: ${goal.weight}`} arrow>
                                                            <Chip 
                                                                label={getScoreLabel(goal.weight)} 
                                                                size="small"
                                                                color={getWeightColor(goal.weight)}
                                                                variant="outlined"
                                                                sx={{ fontWeight: 500 }}
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleEditGoal(goal)}
                                                                sx={{ mr: 0.5 }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteGoal(goal)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Goal Dialog */}
            <AddEditGoalDialog
                open={addEditDialogOpen}
                isEditMode={isEditMode}
                goal={selectedGoal}
                userKpiId={userKpiWithGoals?.id}
                onClose={handleAddEditDialogClose}
                onSubmit={handleAddEditDialogSubmit}
            />

            {/* Delete Goal Confirmation Dialog */}
            <DeleteGoalConfirmDialog
                open={deleteDialogOpen}
                goal={selectedGoal}
                onClose={handleDeleteDialogClose}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default ManageGoalsDialog;
